import React from "react";
import {NPC, Plot} from "../../../server/Plot";
import {QRCodeSVG} from "qrcode.react";

interface Props {
    plot: Plot;
    plotUrl: string;
}

// ── Shared styles ─────────────────────────────────────────────────────────────

const FONT = "Arial, sans-serif";
const border = "1px solid #000";
const borderLight = "1px solid #ccc";

const th: React.CSSProperties = {
    border, padding: "3px 6px", fontWeight: "bold", fontSize: 11,
    verticalAlign: "middle", backgroundColor: "#eee", whiteSpace: "nowrap",
};
const td: React.CSSProperties = {border, padding: "3px 6px", fontSize: 11, verticalAlign: "top"};

const pageFull: React.CSSProperties = {
    height: "10in",
    overflow: "hidden",
    pageBreakAfter: "always",
    fontFamily: FONT,
};

const divider: React.CSSProperties = {borderTop: "2px solid #000", margin: "5px 0 8px 0"};
const sectionHd: React.CSSProperties = {
    fontWeight: "bold", fontSize: 12, margin: "7px 0 3px 0", fontFamily: FONT,
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatStartTime(raw?: string | null): string {
    if (!raw) return "";
    try {
        return new Date(raw).toLocaleString("en-US", {
            timeZone: "America/New_York",
            weekday: "short",
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
            timeZoneName: "short",
        });
    } catch {
        return raw;
    }
}

function formatDuration(mins?: number | null): string {
    if (!mins) return "";
    const h = Math.floor(mins / 60);
    const m = (mins % 60).toString().padStart(2, "0");
    return `${h}h ${m}m`;
}

// ── Shared sub-components ─────────────────────────────────────────────────────

function PageHeader({name, sub}: {name: string; sub?: string}) {
    return (
        <>
            <h1 style={{fontSize: 24, fontWeight: "bold", margin: "0 0 1px 0",
                textAlign: "center", fontFamily: FONT}}>
                {name}
            </h1>
            {sub && (
                <p style={{textAlign: "center", fontSize: 10, color: "#555",
                    margin: "0 0 3px 0", fontFamily: FONT}}>
                    {sub}
                </p>
            )}
            <div style={divider}/>
        </>
    );
}

/**
 * Two-column attributes table rendered from an ordered list of label/value pairs.
 * Pairs are grouped two-per-row. Full-width entries (spanning all 4 columns) are
 * flagged with `wide: true`. Empty values are filtered out before rendering.
 */
function AttrsTable({fields}: {
    fields: Array<{label: string; value?: string | number | null; wide?: boolean}>
}) {
    const present = fields.filter(f => f.value != null && f.value !== "");
    if (present.length === 0) return null;

    const rows: React.ReactElement[] = [];
    let i = 0;
    while (i < present.length) {
        const f = present[i];
        if (f.wide) {
            rows.push(
                <tr key={i}>
                    <td style={th}>{f.label}</td>
                    <td style={td} colSpan={3}>{f.value}</td>
                </tr>
            );
            i++;
        } else {
            const g = present[i + 1];
            if (g && !g.wide) {
                rows.push(
                    <tr key={i}>
                        <td style={th}>{f.label}</td>
                        <td style={td}>{f.value}</td>
                        <td style={th}>{g.label}</td>
                        <td style={td}>{g.value}</td>
                    </tr>
                );
                i += 2;
            } else {
                rows.push(
                    <tr key={i}>
                        <td style={th}>{f.label}</td>
                        <td style={td} colSpan={3}>{f.value}</td>
                    </tr>
                );
                i++;
            }
        }
    }

    return (
        <table style={{width: "100%", borderCollapse: "collapse", marginBottom: 8}}>
            <colgroup>
                <col style={{width: "16%"}}/><col style={{width: "34%"}}/>
                <col style={{width: "16%"}}/><col style={{width: "34%"}}/>
            </colgroup>
            <tbody>{rows}</tbody>
        </table>
    );
}

function TextBlock({title, content}: {title: string; content?: string | null}) {
    if (!content?.trim()) return null;
    return (
        <>
            <p style={sectionHd}>{title}</p>
            <p style={{fontSize: 11, whiteSpace: "pre-wrap", lineHeight: 1.45,
                margin: "0 0 6px 0", fontFamily: FONT}}>
                {content}
            </p>
        </>
    );
}

function LinedSection({title, lines = 12}: {title: string; lines?: number}) {
    return (
        <div style={{marginBottom: "0.16in"}}>
            <p style={{...sectionHd, marginBottom: 4}}>{title}</p>
            {Array.from({length: lines}).map((_, i) => (
                <div key={i} style={{borderBottom: "1px solid #aaa", height: "0.25in"}}/>
            ))}
        </div>
    );
}

/** Blank writable area for fields with no stored value (e.g. Plot Specific Items). */
function WritableSection({title, lines = 5}: {title: string; lines?: number}) {
    return (
        <div style={{marginBottom: "0.1in"}}>
            <p style={sectionHd}>{title}</p>
            {Array.from({length: lines}).map((_, i) => (
                <div key={i} style={{borderBottom: "1px solid #bbb", height: "0.22in"}}/>
            ))}
        </div>
    );
}

// ── NPC card with clear visual sections ───────────────────────────────────────

function NPCCard({npc, role}: {npc: NPC; role?: string}) {
    const ext = npc.extended;
    const hasQuickFacts = ext?.played_by || ext?.appearance || ext?.groups || ext?.species_type;

    return (
        <div style={{
            border: "1px solid #666",
            borderRadius: 3,
            marginBottom: "0.12in",
            overflow: "hidden",
            fontFamily: FONT,
            pageBreakInside: "avoid",
        }}>
            {/* ── Name bar ── */}
            <div style={{
                backgroundColor: "#333",
                color: "#fff",
                padding: "4px 8px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
            }}>
                <span style={{fontWeight: "bold", fontSize: 13}}>{npc.name}</span>
                <span style={{fontSize: 10, opacity: 0.85}}>
                    {[role, ext?.species_type].filter(Boolean).join(" · ")}
                </span>
            </div>

            <div style={{padding: "5px 8px"}}>
                {/* ── Quick facts ── */}
                {hasQuickFacts && (
                    <div style={{
                        display: "flex", gap: "0.25in", flexWrap: "wrap",
                        fontSize: 10, marginBottom: 5,
                        paddingBottom: 4, borderBottom: borderLight,
                    }}>
                        {ext?.played_by  && <span><strong>Played By:</strong> {ext.played_by}</span>}
                        {ext?.groups     && <span><strong>Groups:</strong> {ext.groups}</span>}
                        {ext?.appearance && <span><strong>Appearance:</strong> {ext.appearance}</span>}
                    </div>
                )}

                {/* ── Personality ── */}
                {ext?.personality && (
                    <div style={{marginBottom: ext?.history ? 5 : 0}}>
                        <div style={{
                            fontSize: 10, fontWeight: "bold", textTransform: "uppercase",
                            letterSpacing: 0.5, color: "#444",
                            borderBottom: borderLight, marginBottom: 2, paddingBottom: 1,
                        }}>
                            Personality
                        </div>
                        <p style={{fontSize: 11, whiteSpace: "pre-wrap", lineHeight: 1.4, margin: 0}}>
                            {ext.personality}
                        </p>
                    </div>
                )}

                {/* ── History ── */}
                {ext?.history && (
                    <div style={{marginTop: ext?.personality ? 4 : 0}}>
                        <div style={{
                            fontSize: 10, fontWeight: "bold", textTransform: "uppercase",
                            letterSpacing: 0.5, color: "#444",
                            borderBottom: borderLight, marginBottom: 2, paddingBottom: 1,
                        }}>
                            History
                        </div>
                        <p style={{fontSize: 11, whiteSpace: "pre-wrap", lineHeight: 1.4, margin: 0}}>
                            {ext.history}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}

// ── Main export ───────────────────────────────────────────────────────────────

export function PlotPrint({plot, plotUrl}: Props) {
    const monsters       = plot.plotCharacters?.monsters_requested     ?? [];
    const namedMonsters  = plot.plotCharacters?.namedMonstersRequested ?? [];
    const additionalNpcs = plot.plotCharacters?.additional_npcs        ?? [];

    // Mirror PlotViewer.peopleNeeded() using server-side plotCharacters data
    const peopleNeeded =
        (plot.hook ? 1 : 0) +
        additionalNpcs.length +
        monsters.reduce((s, m) => s + m.quantity, 0) +
        namedMonsters.filter(m => (m.monster as any).dedicated_marshal).length;

    const statusLine = [plot.status, plot.event?.name].filter(Boolean).join(" — ");

    // ── Page 1: Attributes, Setup, Description & Monsters ────────────────────

    const page1 = (
        <div style={pageFull}>
            <PageHeader name={plot.name} sub={statusLine}/>

            <AttrsTable fields={[
                {label: "Writer",          value: plot.writer},
                {label: "Team Lead",       value: plot.lead},
                {label: "Type",            value: plot.type},
                {label: "Run",             value: plot.run},
                {label: "Duration",        value: formatDuration(plot.estimated_duration)},
                {label: "People Needed",   value: peopleNeeded > 0 ? peopleNeeded : null},
                {label: "Action Location", value: plot.action_location},
                {label: "Hook Location",   value: plot.hook_location},
                {label: "Start Time",      value: formatStartTime(plot.start_time), wide: true},
                {label: "Run Info",        value: plot.info,                        wide: true},
            ]}/>

            {(plot.tags?.length ?? 0) > 0 && (
                <div style={{marginBottom: 6, fontSize: 11, fontFamily: FONT}}>
                    <strong>Tags:</strong>{" "}{plot.tags!.map(t => t.name).join(", ")}
                </div>
            )}

            <TextBlock title="Summary"     content={plot.short_desc}/>
            <TextBlock title="Setup"       content={plot.setup}/>
            {plot.setup_items?.trim()
                ? <TextBlock title="Plot Specific Items" content={plot.setup_items}/>
                : <WritableSection title="Plot Specific Items" lines={5}/>
            }
            <TextBlock title="Description" content={plot.description}/>

            {(monsters.length > 0 || namedMonsters.length > 0) && (
                <>
                    <p style={sectionHd}>Assigned Monsters</p>
                    <table style={{width: "100%", borderCollapse: "collapse"}}>
                        <colgroup>
                            <col style={{width: "70%"}}/><col style={{width: "30%"}}/>
                        </colgroup>
                        <tbody>
                        <tr>
                            <td style={th}>Monster</td>
                            <td style={{...th, textAlign: "center"}}>Qty</td>
                        </tr>
                        {monsters.map((m, i) => (
                            <tr key={i}>
                                <td style={td}>
                                    {m.type.species?.name
                                        ? `${m.type.species.name} — ${m.type.name}`
                                        : m.type.name}
                                </td>
                                <td style={{...td, textAlign: "center"}}>{m.quantity}</td>
                            </tr>
                        ))}
                        {namedMonsters.map((m, i) => (
                            <tr key={`nm-${i}`}>
                                <td style={td}>
                                    {m.monster.name}
                                    {(m.monster as any).dedicated_marshal && (
                                        <em style={{fontSize: 10, color: "#555", marginLeft: 6}}>
                                            (Ded. Marshal)
                                        </em>
                                    )}
                                </td>
                                <td style={{...td, textAlign: "center"}}>{m.quantity}</td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </>
            )}
        </div>
    );

    // ── Page 2: Hook & Additional NPCs ────────────────────────────────────────

    const page2 = (
        <div style={pageFull}>
            <PageHeader name={plot.name} sub="Hook & NPCs"/>

            {plot.hook && (
                <>
                    <p style={{...sectionHd, marginTop: 0}}>Hook NPC</p>
                    <NPCCard npc={plot.hook} role="Hook"/>
                </>
            )}

            {additionalNpcs.length > 0 && (
                <>
                    <p style={sectionHd}>Additional NPCs</p>
                    {additionalNpcs.map((npc, i) => (
                        <NPCCard key={npc.id ?? i} npc={npc} role="NPC"/>
                    ))}
                </>
            )}

            {!plot.hook && additionalNpcs.length === 0 && (
                <p style={{fontSize: 11, color: "#888", fontStyle: "italic", fontFamily: FONT}}>
                    No NPCs assigned to this plot.
                </p>
            )}
        </div>
    );

    // ── Page 3: Story Notes ───────────────────────────────────────────────────

    const page3 = (
        <div style={{
            height: "10in",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            fontFamily: FONT,
        }}>
            <PageHeader name={plot.name} sub="Story Notes"/>

            {plot.story_who?.trim() && (
                <div style={{marginBottom: 8}}>
                    <p style={{...sectionHd, marginTop: 0}}>Who Was Involved</p>
                    <p style={{fontSize: 11, whiteSpace: "pre-wrap", lineHeight: 1.5, margin: 0}}>
                        {plot.story_who}
                    </p>
                </div>
            )}

            {!plot.story_who?.trim() && (
                <LinedSection title="Players Involved" lines={2}/>
            )}

            <LinedSection title="Outcome"   lines={12}/>
            <LinedSection title="Follow-up" lines={12}/>

            <div style={{flex: 1}}/>

            <div style={{textAlign: "center", borderTop: borderLight, paddingTop: 8}}>
                <p style={{fontSize: 9, color: "#666", margin: "0 0 4px 0"}}>
                    Scan to open this plot in the web app
                </p>
                <QRCodeSVG value={plotUrl} size={80}/>
                <p style={{fontSize: 9, color: "#999", margin: "4px 0 0 0"}}>{plotUrl}</p>
            </div>
        </div>
    );

    return (
        <>
            {page1}
            {page2}
            {page3}
        </>
    );
}
